/**
 * Usage trends chart — tokens + cost over time, dual Y-axis.
 *
 * recharts stays (`@coordio/ui` has no charting layer); every colour comes from
 * {@link useChartTokens} so the series follow the Cortex theme. Supports area, line,
 * and bar encodings so the same data can be read as a trend or a per-day comparison.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@coordio/ui';
import { EmptyState, LoadingSkeleton } from '@coordio/ui/layout';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { dashboardService } from '../../services/dashboardService';
import { useContextStore } from '../../store/contextStore';
import type { UsageTrendDto } from '../../types/dashboard';
import { chartAxisTick, chartTooltipStyle, useChartTokens } from './useChartTokens';

import './dashboardWidgets.scss';

export type UsageChartType = 'area' | 'line' | 'bar';

export type UsageTrendPoint = {
  date: string;
  tokens: number;
  cost: number;
  sessions: number;
  executions: number;
};

type UsageTrendsChartProps = {
  /** Pre-fetched points. When omitted the chart loads the last `rangeDays` itself. */
  data?: UsageTrendPoint[];
  loading?: boolean;
  rangeDays?: number;
  defaultChartType?: UsageChartType;
  /** Hide the area/line/bar toggle (e.g. when the parent already offers one). */
  hideTypeToggle?: boolean;
  className?: string;
};

const CHART_TYPES: { id: UsageChartType; label: string }[] = [
  { id: 'area', label: 'Area' },
  { id: 'line', label: 'Line' },
  { id: 'bar', label: 'Bar' },
];

const formatTokens = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return String(value);
};

const formatCost = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

const toChartPoints = (rows: UsageTrendDto[]): UsageTrendPoint[] =>
  rows.map((p) => ({
    date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    tokens: p.tokens,
    cost: Number(p.cost),
    sessions: p.sessions,
    executions: p.executions,
  }));

const rangeToUtc = (rangeDays: number) => {
  const end = new Date();
  const start = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000);
  const toYmd = (d: Date) => d.toISOString().slice(0, 10);
  return { startDateUtc: toYmd(start), endDateUtc: toYmd(end) };
};

/**
 * Dual-axis usage chart (tokens left, cost right) with Cortex token colours.
 */
export const UsageTrendsChart = ({
  data: dataProp,
  loading: loadingProp,
  rangeDays = 30,
  defaultChartType = 'area',
  hideTypeToggle = false,
  className,
}: UsageTrendsChartProps) => {
  const tokens = useChartTokens();
  const [chartType, setChartType] = useState<UsageChartType>(defaultChartType);
  const { currentTenant, currentProject } = useContextStore();
  const hasTenant = !!currentTenant?.id;
  const shouldFetch = dataProp === undefined;

  const { startDateUtc, endDateUtc } = useMemo(() => rangeToUtc(rangeDays), [rangeDays]);

  const { data: fetched, isLoading: fetchLoading } = useQuery({
    queryKey: ['dashboard', 'usageTrends', currentTenant?.id, currentProject?.id, startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getUsageTrends(startDateUtc, endDateUtc),
    enabled: shouldFetch && hasTenant,
  });

  const chartData = dataProp ?? toChartPoints(fetched ?? []);
  const loading = loadingProp ?? (shouldFetch && fetchLoading);

  const sharedAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
      <XAxis dataKey="date" tick={chartAxisTick(tokens)} axisLine={false} tickLine={false} minTickGap={28} />
      <YAxis
        yAxisId="left"
        tickFormatter={formatTokens}
        tick={chartAxisTick(tokens)}
        axisLine={false}
        tickLine={false}
        width={48}
      />
      <YAxis
        yAxisId="right"
        orientation="right"
        tickFormatter={formatCost}
        tick={chartAxisTick(tokens)}
        axisLine={false}
        tickLine={false}
        width={56}
      />
      <RechartsTooltip
        contentStyle={chartTooltipStyle(tokens)}
        formatter={(value, name) => {
          const n = Number(value);
          if (name === 'Cost') return [formatCost(n), name];
          if (name === 'Tokens') return [n.toLocaleString(), name];
          return [n.toLocaleString(), String(name)];
        }}
      />
      <Legend wrapperStyle={{ fontSize: 12, color: tokens.axis }} />
    </>
  );

  const renderSeries = (mode: UsageChartType) => {
    if (mode === 'bar') {
      return (
        <>
          <Bar yAxisId="left" dataKey="tokens" name="Tokens" fill={tokens.series[0]} radius={[2, 2, 0, 0]} />
          <Bar yAxisId="right" dataKey="cost" name="Cost" fill={tokens.series[1]} radius={[2, 2, 0, 0]} />
        </>
      );
    }

    if (mode === 'line') {
      return (
        <>
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="tokens"
            name="Tokens"
            stroke={tokens.series[0]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="cost"
            name="Cost"
            stroke={tokens.series[1]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3 }}
          />
        </>
      );
    }

    return (
      <>
        <defs>
          <linearGradient id="usageTokensGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tokens.series[0]} stopOpacity={0.3} />
            <stop offset="100%" stopColor={tokens.series[0]} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="usageCostGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tokens.series[1]} stopOpacity={0.3} />
            <stop offset="100%" stopColor={tokens.series[1]} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          yAxisId="left"
          type="monotone"
          dataKey="tokens"
          name="Tokens"
          stroke={tokens.series[0]}
          strokeWidth={2}
          fill="url(#usageTokensGradient)"
          dot={false}
        />
        <Area
          yAxisId="right"
          type="monotone"
          dataKey="cost"
          name="Cost"
          stroke={tokens.series[1]}
          strokeWidth={2}
          fill="url(#usageCostGradient)"
          dot={false}
        />
      </>
    );
  };

  return (
    <div className={className ? `usage-trends-chart ${className}` : 'usage-trends-chart'} data-testid="usage-trends-chart">
      {!hideTypeToggle && (
        <div className="usage-trends-chart__toolbar" role="group" aria-label="Chart type">
          {CHART_TYPES.map((opt) => (
            <Button
              key={opt.id}
              type="button"
              size="sm"
              variant={chartType === opt.id ? 'default' : 'outline'}
              onClick={() => setChartType(opt.id)}
              data-testid={`usage-trends-type-${opt.id}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      )}

      <div className="usage-trends-chart__plot">
        {loading ? (
          <LoadingSkeleton rows={4} />
        ) : chartData.length === 0 ? (
          <EmptyState
            title="No usage yet"
            description="Token and cost trends appear once agents start running in this range."
          />
        ) : chartType === 'bar' ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={2} barCategoryGap="18%">
              {sharedAxes}
              {renderSeries('bar')}
            </BarChart>
          </ResponsiveContainer>
        ) : chartType === 'line' ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              {sharedAxes}
              {renderSeries('line')}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              {sharedAxes}
              {renderSeries('area')}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export { toChartPoints };
